import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'
import {BUSINESSES_PAGE_SIZE} from '@/hooks/empresa/organizacion/use-businesses'

export type BillingState = components['schemas']['BillingStateRead']
export type BillingInvoice = components['schemas']['BillingInvoiceRead']
export type BillingCycle = components['schemas']['BillingCycle']
export type BillingSubscriptionEstado = components['schemas']['BillingSubscriptionEstado']
export type BillingPaymentSource = components['schemas']['BillingPaymentSourceRead']
export type PlanPrice = components['schemas']['PlanPriceRead']
export type BillingCycleState = components['schemas']['BillingCycleRead']
export type UpgradePreview = components['schemas']['UpgradePreviewRead']
export type UpgradeInvoice = components['schemas']['UpgradeInvoiceRead']
type SubscriptionCreateRead = components['schemas']['SubscriptionCreateRead']
type InvoiceLinkRead = components['schemas']['InvoiceLinkRead']
type Page<T> = Omit<components['schemas']['Page_BillingInvoiceRead_'], 'items'> & {items: T[]}

/** Los mismos parámetros del acto bloqueado, uno solo por acto: el segmento lo deriva el backend. */
export type UpgradeProposal = {
  proposed_risk_classification_id?: string | null
  proposed_active_delta?: number
  proposed_declared_worker_count?: number | null
}

/** Solo tokens de Wompi y datos de presentación: nunca PAN ni CVC. */
export type PaymentSourcePayload = {
  tipo: 'tarjeta' | 'nequi'
  token: string
  acceptance_token: string
  accept_personal_auth: string
  customer_email: string
  brand: string | null
  last4: string | null
  exp_month: string | null
  exp_year: string | null
}

export function useBillingState(businessId: string | undefined) {
  return useQuery({
    queryKey: ['billing', businessId],
    queryFn: async () => (await api.get<BillingState>(`/businesses/${businessId}/billing`)).data,
    enabled: !!businessId,
  })
}

export function useBillingInvoices(businessId: string | undefined, page: number) {
  return useQuery({
    queryKey: ['billing', businessId, 'invoices', page],
    queryFn: async () =>
      (
        await api.get<Page<BillingInvoice>>(`/businesses/${businessId}/billing/invoices`, {
          params: {page, page_size: BUSINESSES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

/** Con `payment_source` la activación queda recurrente en una sola pasada; sin él el
 * backend mintea el enlace de pago. */
export type SubscriptionActivation = {
  billing_cycle: BillingCycle
  payment_source?: PaymentSourcePayload
}

export function useCreateSubscription(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (activation: SubscriptionActivation) =>
      (await api.post<SubscriptionCreateRead>(`/businesses/${businessId}/billing/subscription`, activation)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['billing', businessId]})
    },
  })
}

export function useRemintInvoiceLink(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (invoiceId: string) =>
      (await api.post<InvoiceLinkRead>(`/businesses/${businessId}/billing/invoices/${invoiceId}/link`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['billing', businessId, 'invoices']})
    },
  })
}

export function usePutPaymentSource(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PaymentSourcePayload) =>
      (await api.put<BillingPaymentSource>(`/businesses/${businessId}/billing/payment-source`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['billing', businessId]})
    },
  })
}

export function useDeletePaymentSource(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/businesses/${businessId}/billing/payment-source`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['billing', businessId]})
    },
  })
}

export function useBillingCycle(businessId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['billing', businessId, 'cycle'],
    queryFn: async () => (await api.get<BillingCycleState>(`/businesses/${businessId}/billing/cycle`)).data,
    enabled: !!businessId && enabled,
  })
}

export function useUpdateBillingCycle(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (billingCycle: BillingCycle) =>
      (await api.patch<BillingCycleState>(`/businesses/${businessId}/billing/cycle`, {billing_cycle: billingCycle}))
        .data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['billing', businessId]})
    },
  })
}

/** Función y no consulta: el modal la llama una vez al recibir un 409. */
export async function fetchUpgradePreview(businessId: string, proposal: UpgradeProposal): Promise<UpgradePreview> {
  const params: Record<string, string> = {}
  if (proposal.proposed_risk_classification_id)
    params.proposed_risk_classification_id = proposal.proposed_risk_classification_id
  if (proposal.proposed_active_delta) params.proposed_active_delta = String(proposal.proposed_active_delta)
  if (proposal.proposed_declared_worker_count != null)
    params.proposed_declared_worker_count = String(proposal.proposed_declared_worker_count)
  return (await api.get<UpgradePreview>(`/businesses/${businessId}/billing/upgrade-preview`, {params})).data
}

export function useCreateUpgrade(businessId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (proposal: UpgradeProposal) =>
      (await api.post<UpgradeInvoice>(`/businesses/${businessId}/billing/upgrade`, proposal)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['billing', businessId]})
    },
  })
}
